import axios from 'axios';
import { authApi } from '@/services/api';
import { secureStorage } from '@/utils/secureStorage';

export type AdminRefreshResult = {
  token: string;
  refresh_token: string;
  admin?: Record<string, unknown>;
};

let refreshInFlight: Promise<AdminRefreshResult | null> | null = null;

export function syncAuthorizationHeader(token: string): void {
  axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}

export function applySessionTokens(accessToken: string, refreshToken?: string): void {
  secureStorage.setToken(accessToken);
  syncAuthorizationHeader(accessToken);
  if (refreshToken) {
    secureStorage.setRefreshToken(refreshToken);
  }
}

export async function refreshAdminSession(): Promise<AdminRefreshResult | null> {
  if (!secureStorage.getIsAdmin()) {
    return null;
  }

  const refreshToken = secureStorage.getRefreshToken();
  if (!refreshToken) {
    return null;
  }

  if (!refreshInFlight) {
    refreshInFlight = authApi
      .adminRefresh(refreshToken)
      .then((data: { token?: string; refresh_token?: string; admin?: Record<string, unknown> }) => {
        if (!data?.token) {
          return null;
        }
        return {
          token: data.token,
          refresh_token: data.refresh_token ?? refreshToken,
          admin: data.admin,
        };
      })
      .catch(() => null)
      .finally(() => {
        refreshInFlight = null;
      });
  }

  return refreshInFlight;
}

export async function ensureValidAccessToken(): Promise<AdminRefreshResult | null> {
  const stored = secureStorage.getToken();
  if (stored && !secureStorage.isTokenExpired()) {
    syncAuthorizationHeader(stored);
    return { token: stored, refresh_token: secureStorage.getRefreshToken() ?? '' };
  }

  const refreshed = await refreshAdminSession();
  if (!refreshed) {
    return null;
  }

  applySessionTokens(refreshed.token, refreshed.refresh_token);
  return refreshed;
}

export function clearAdminSessionStorage(): void {
  secureStorage.removeToken();
  secureStorage.removeRefreshToken();
  secureStorage.removeIsAdmin();
  delete axios.defaults.headers.common['Authorization'];
}
